import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight, CheckCircle2, Trash2 } from "lucide-react";
import { useTradeWorkflows } from "@/hooks/useTradeWorkflows";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JobTrade {
  id: string;
  trade_code: string;
  status: string;
  subtotal: number;
  trades?: {
    name: string;
    icon: string;
  };
}

interface TradeCardProps {
  jobTrade: JobTrade;
  propertyType: string;
  onViewDetails: () => void;
  onDelete: (tradeId: string) => void;
}

export const TradeCard = ({ jobTrade, propertyType, onViewDetails, onDelete }: TradeCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { stages, loading } = useTradeWorkflows(propertyType, jobTrade.trade_code);
  
  const tradeName = jobTrade.trades?.name || jobTrade.trade_code;
  const tradeIcon = jobTrade.trades?.icon || '🔧';
  const currentStatus = jobTrade.status;
  const subtotal = jobTrade.subtotal || 0;

  // Calculate progress percentage
  const currentStageIndex = stages.findIndex(s => s.stage_key === currentStatus);
  const progressPercentage = stages.length > 0 
    ? ((currentStageIndex + 1) / stages.length) * 100 
    : 0;

  const isComplete = currentStatus === 'complete' || currentStatus === 'completed';

  return (
    <Card className="hover:shadow-md transition-shadow border-border/50">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{tradeIcon}</div>
            <div>
              <h3 className="font-semibold text-lg">{tradeName}</h3>
              {isComplete && (
                <Badge variant="outline" className="mt-1 bg-success/10 text-success border-success/20">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Complete
                </Badge>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onViewDetails}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress Bar */}
        {!loading && stages.length > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>{stages[currentStageIndex]?.stage_name || 'New'}</span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Current Stage Badge */}
        {!isComplete && (
          <Badge variant="secondary" className="mb-3">
            {stages[currentStageIndex]?.stage_name || currentStatus}
          </Badge>
        )}

        {/* Subtotal */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Subtotal</span>
          <span className="font-semibold text-lg">
            ${subtotal.toLocaleString()}
          </span>
        </div>

        <div className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={onViewDetails}
          >
            Manage Trade
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {tradeName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this trade and all associated measurements, estimates, and proposals. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                onDelete(jobTrade.id);
                setShowDeleteDialog(false);
              }}
            >
              Delete Trade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
